import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Notification } from "../common/Notification";
import { useForm } from "react-hook-form";
import { addSeniorFamilyMember, updateSeniorFamilyMember } from "../../store/service";
import * as FamilyServiceActions from "../../store/familyService/actions";
import { ApplicationState } from "../../store";
import { IFamilyServiceMember } from "../../store/familyService/types";
import { PATTERNS } from "../../util/patterns";
import { useTranslation } from "react-i18next";

const SeniorsFraudResolutionDialog: React.FunctionComponent<{ closeDialog(): void; seniorMember?: IFamilyServiceMember }> = (props) => {
    const [isLoading, setisLoading] = useState(false);
    const dispatch = useDispatch();
    const { t: tLang } = useTranslation();
    const getSeniorFamilyMember = () => dispatch(FamilyServiceActions.actionCreators.getSeniorFamilyMember());
    const seniorMemberList = useSelector((state: ApplicationState) =>
        state.familyServiceState && state.familyServiceState.seniorMemberList ? state.familyServiceState.seniorMemberList : []
    );
    const [seniorsAccount, setSeniorsAccount] = useState({
        relationshipType: props.seniorMember && props.seniorMember.relationshipType ? props.seniorMember.relationshipType : "",
        firstName: props.seniorMember && props.seniorMember.firstName ? props.seniorMember.firstName : "",
        lastName: props.seniorMember && props.seniorMember.lastName ? props.seniorMember.lastName : "",
        islivesInHouseHold: props.seniorMember && props.seniorMember.islivesInHouseHold === false ? "0" : "1",
        streetNumber: props.seniorMember && props.seniorMember.streetNumber ? props.seniorMember.streetNumber : "",
        streetName: props.seniorMember && props.seniorMember.streetName ? props.seniorMember.streetName : "",
    });
    const isNew = props.seniorMember && props.seniorMember.id ? false : true;

    const { register, errors, handleSubmit } = useForm({
        mode: "onBlur",
        reValidateMode: "onChange",
    });

    const closeDialog = () => {
        document.body.classList.remove("no_scroll");
        props.closeDialog();
    };

    const refreshSeniorMembersInformation = () => {
        closeDialog();
        getSeniorFamilyMember();
    };

    function onChangeHandler(event: any) {
        const name = event.currentTarget.name;
        setSeniorsAccount({ ...seniorsAccount, [name as string]: event.currentTarget.value });
    }

    useEffect(() => {
        if (seniorsAccount.islivesInHouseHold === "1") {
            setSeniorsAccount({ ...seniorsAccount, streetName: "", streetNumber: "" });
        }
    }, [seniorsAccount.islivesInHouseHold]);

    const saveItem = () => {
        if (isNew) {
            addSenior();
        } else {
            if (props.seniorMember) {
                const requestBody = {
                    id: props.seniorMember.id,
                    firstName: seniorsAccount.firstName,
                    lastName: seniorsAccount.lastName,
                    email: props.seniorMember.email,
                    parentUserId: props.seniorMember.parentUserId,
                    parentEzshieldId: props.seniorMember.parentEzshieldId,
                    partner: props.seniorMember.partner,
                    provisionalID: props.seniorMember.provisionalID,
                    streetNumber: seniorsAccount.streetNumber,
                    streetName: seniorsAccount.streetName,
                    relationshipType: seniorsAccount.relationshipType,
                    islivesInHouseHold: seniorsAccount.islivesInHouseHold === "1" ? true : false,
                    createdTimeStamp: props.seniorMember.createdTimeStamp,
                    updatedTimeStamp: props.seniorMember.updatedTimeStamp,
                    syncTimeStamp: props.seniorMember.syncTimeStamp,
                    syncStatus: props.seniorMember.syncStatus,
                    isDisassociated: props.seniorMember.isDisassociated,
                    fk_RelationshipMembersType: props.seniorMember.fk_RelationshipMembersType,
                };

                setisLoading(true);

                updateSeniorFamilyMember(requestBody)
                    .then((data) => {
                        setisLoading(false);
                        if (data.isSuccess) {
                            Notification({
                                type: "success",
                                title: "Update Senior Member",
                                message: seniorsAccount.firstName + " " + seniorsAccount.lastName + " information updated successfully.",
                            });
                            refreshSeniorMembersInformation();
                        } else {
                            Notification({
                                type: "error",
                                title: "Update Senior Member Error",
                                message: "Could not update senior family member. Please Try again later.",
                            });
                            refreshSeniorMembersInformation();
                        }
                    })
                    .catch(function (error) {
                        setisLoading(false);
                        Notification({
                            type: "error",
                            title: "Error",
                            message: "We're sorry.We were unable to update your item.Please try again.",
                        });
                    });
            }
        }
    };

    const addSenior = () => {
        const requestBody = {
            relationshipType: seniorsAccount.relationshipType,
            firstName: seniorsAccount.firstName,
            lastName: seniorsAccount.lastName,
            islivesInHouseHold: Number(seniorsAccount.islivesInHouseHold),
            streetName: seniorsAccount.streetName,
            streetNumber: seniorsAccount.streetNumber,
        };

        if (seniorMemberList.length < 8) {
            setisLoading(true);
            addSeniorFamilyMember(requestBody)
                .then((data) => {
                    setisLoading(false);
                    if (data.isSuccess) {
                        Notification({
                            type: "success",
                            title: "Add Senior Member",
                            message: seniorsAccount.firstName + " " + seniorsAccount.lastName + " added successfully.",
                        });
                        refreshSeniorMembersInformation();
                    } else {
                        Notification({
                            type: "error",
                            title: "Add Senior Member Error",
                            message: "Could not add senior family member. Please Try again later.",
                        });
                        refreshSeniorMembersInformation();
                    }
                })
                .catch(function (error) {
                    setisLoading(false);
                    Notification({
                        type: "error",
                        title: "Error",
                        message: "We're sorry.We were unable to add your item.Please try again.",
                    });
                });
        }
    };

    return (
        <React.Fragment>
            {!isLoading ? (
                <>
                    <div className="dialog-header">
                        <div className="dialog-icon">
                            <em className={`las ${isNew ? "la-user-plus" : "la-user"}`}></em>
                        </div>
                        <h1>{isNew ? "Add a Senior to Protect" : `Update ${seniorsAccount.firstName} ${seniorsAccount.lastName}`}</h1>
                        <br />
                        {isNew === true ? (
                            <>
                                <p>
                                    {" "}
                                    Your plan includes Fraud Resolution Services for Seniors that you look out for, including your parents, parents-in-law,
                                    grandparents and grandparents-in-law.
                                </p>
                                <br />
                                <p>Family plans include Expense Reimbursement Insurance for your Seniors as well.</p>
                                <br />
                            </>
                        ) : (
                            <></>
                        )}
                    </div>

                    <form onSubmit={handleSubmit(saveItem)} id="add-sfr-form-container">
                        <div className="dialog-body">
                            <div className={errors["relationshipType"] ? "form-row row status-error" : "form-row row"}>
                                <div className="form-row inline row">
                                    <div className="form-label col-xs-12 col-sm-5 col-no-gutter">
                                        <label htmlFor="edit-sfr-relationship-type">
                                            Relationship{" "}
                                            <span className="required" title="Mandatory">
                                                *
                                            </span>
                                        </label>
                                    </div>
                                    <div className="form-input col-xs-12 col-sm-7 col-no-gutter">
                                        <select
                                            id="edit-sfr-relationship-type"
                                            aria-labelledby="edit-sfr-relationship-type"
                                            className="inputbox"
                                            name="relationshipType"
                                            defaultValue={seniorsAccount.relationshipType}
                                            onChange={onChangeHandler}
                                            ref={register({
                                                required: true,
                                            })}
                                        >
                                            <option value="">- Select One -</option>
                                            <option value="parent">Parent</option>
                                            <option value="parent-in-law">Parent-In-Law</option>
                                            <option value="grandparent">Grandparent</option>
                                            <option value="grandparent-in-law">Grandparent-In-Law</option>
                                        </select>
                                        {errors.relationshipType && errors.relationshipType.type === "required" && (
                                            <span className="form-error">Relationship is required.</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className={errors["firstName"] ? "form-row row status-error" : "form-row row"}>
                                <div className="form-row inline row">
                                    <div className="form-label col-xs-12 col-sm-5 col-no-gutter">
                                        <label htmlFor="edit-first-name">
                                            First Name{" "}
                                            <span className="required" title="Mandatory">
                                                *
                                            </span>
                                        </label>
                                    </div>
                                    <div className="form-input col-xs-12 col-sm-7 col-no-gutter">
                                        <input
                                            type="text"
                                            className={errors.firstName ? "status-error inputbox" : "inputbox"}
                                            id="edit-first-name"
                                            aria-labelledby="edit-first-name"
                                            name="firstName"
                                            defaultValue={seniorsAccount.firstName ? seniorsAccount.firstName : ""}
                                            onChange={onChangeHandler}
                                            ref={register({
                                                required: true,
                                                pattern: PATTERNS.FIRSTNAME.pattern,
                                            })}
                                            maxLength={30}
                                            aria-required="true"
                                        />
                                        {errors.firstName && errors.firstName.type === "pattern" && (
                                            <span className="form-error">{tLang(PATTERNS.FIRSTNAME.message)}</span>
                                        )}
                                        {errors.firstName && errors.firstName.type === "required" && <span className="form-error">First Name is required.</span>}
                                    </div>
                                </div>
                            </div>

                            <div className={errors["lastName"] ? "form-row row status-error" : "form-row row"}>
                                <div className="form-row inline row">
                                    <div className="form-label col-xs-12 col-sm-5 col-no-gutter">
                                        <label htmlFor="edit-last-name">
                                            Last Name{" "}
                                            <span className="required" title="Mandatory">
                                                *
                                            </span>
                                        </label>
                                    </div>
                                    <div className="form-input col-xs-12 col-sm-7 col-no-gutter">
                                        <input
                                            type="text"
                                            className="inputbox"
                                            id="edit-last-name"
                                            aria-labelledby="edit-last-name"
                                            name="lastName"
                                            defaultValue={seniorsAccount.lastName ? seniorsAccount.lastName : ""}
                                            onChange={onChangeHandler}
                                            ref={register({
                                                required: true,
                                                pattern: PATTERNS.LASTNAME.pattern,
                                            })}
                                            maxLength={30}
                                            aria-required="true"
                                        />
                                        {errors.lastName && errors.lastName.type === "pattern" && <span className="form-error">{tLang(PATTERNS.LASTNAME.message)}</span>}

                                        {errors.lastName && errors.lastName.type === "required" && <span className="form-error">Last Name is required.</span>}
                                    </div>
                                </div>
                            </div>
                            <div className="form-row inline">
                                <div className="row">
                                    <div className="form-label col-xs-12 col-sm-5 col-no-gutter">
                                        <legend className="legend-label">
                                            Lives in your household?{" "}
                                            <span className="required" title="Mandatory">
                                                *
                                            </span>
                                        </legend>
                                    </div>

                                    <div className="col-xs-12 col-sm-7 col-no-gutter">
                                        <div className="row">
                                            <div className={errors["islivesInHouseHold"] ? "form-row row status-error" : "form-row row"}>
                                                <div className="col-xs-12 col-no-gutter">
                                                    <div className="form-row inline">
                                                        <div className="form-input margin-right-xs">
                                                            <label className="form-radio-label" htmlFor="edit-sfr-same-household-yes">
                                                                <input
                                                                    type="radio"
                                                                    id="edit-sfr-same-household-yes"
                                                                    aria-labelledby="edit-sfr-same-household-yes"
                                                                    name="islivesInHouseHold"
                                                                    value="1"
                                                                    defaultChecked={seniorsAccount.islivesInHouseHold === "1"}
                                                                    onChange={onChangeHandler}
                                                                    ref={register({
                                                                        required: true,
                                                                    })}
                                                                />
                                                                <span className="form-radio-label-text">Yes</span>
                                                            </label>
                                                        </div>
                                                        <div className="form-input">
                                                            <label className="form-radio-label" htmlFor="edit-sfr-same-household-no">
                                                                <input
                                                                    type="radio"
                                                                    id="edit-sfr-same-household-no"
                                                                    aria-labelledby="edit-sfr-same-household-no"
                                                                    name="islivesInHouseHold"
                                                                    value="0"
                                                                    defaultChecked={seniorsAccount.islivesInHouseHold === "0"}
                                                                    onChange={onChangeHandler}
                                                                    ref={register({
                                                                        required: true,
                                                                    })}
                                                                />
                                                                <span className="form-radio-label-text">No</span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>

                                                {errors.islivesInHouseHold && errors.islivesInHouseHold.type === "required" && (
                                                    <span className="form-error">Lives in your household is required.</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {seniorsAccount.islivesInHouseHold === "0" && (
                                <div>
                                    <div className={errors["streetNumber"] ? "form-row row status-error" : "form-row row"}>
                                        <div className="form-row inline row">
                                            <div className="form-label col-xs-12 col-sm-5 col-no-gutter">
                                                <label htmlFor="edit-street-number">
                                                    Senior's Street Number{" "}
                                                    <span className="required" title="Mandatory">
                                                        *
                                                    </span>
                                                </label>
                                            </div>
                                            <div className="form-input col-xs-12 col-sm-7 col-no-gutter">
                                                <input
                                                    type="text"
                                                    className="inputbox"
                                                    id="edit-street-number"
                                                    aria-labelledby="edit-street-number"
                                                    name="streetNumber"
                                                    defaultValue={seniorsAccount.streetNumber}
                                                    onChange={onChangeHandler}
                                                    ref={register({
                                                        required: true,
                                                        pattern: /^[a-zA-Z0-9-\s]+$/,
                                                    })}
                                                    maxLength={100}
                                                    aria-required="true"
                                                />
                                                {errors.streetNumber && errors.streetNumber.type === "pattern" && (
                                                    <span className="form-error">
                                                        Invalid format. Senior's Street Number can only include letters, numbers, spaces and the special characters:
                                                        -
                                                    </span>
                                                )}
                                                {errors.streetNumber && errors.streetNumber.type === "required" && (
                                                    <span className="form-error">Senior's Street Number is required.</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className={errors["streetName"] ? "form-row row status-error" : "form-row row"}>
                                        <div className="form-row inline row">
                                            <div className="form-label col-xs-12 col-sm-5 col-no-gutter">
                                                <label htmlFor="edit-street-name">
                                                    Senior's Street Name{" "}
                                                    <span className="required" title="Mandatory">
                                                        *
                                                    </span>
                                                </label>
                                            </div>
                                            <div className="form-input col-xs-12 col-sm-7 col-no-gutter">
                                                <input
                                                    type="text"
                                                    className="inputbox"
                                                    id="edit-street-name"
                                                    aria-labelledby="edit-street-name"
                                                    name="streetName"
                                                    defaultValue={seniorsAccount.streetName}
                                                    onChange={onChangeHandler}
                                                    ref={register({
                                                        required: true,
                                                        pattern: /^[a-zA-Z0-9-\s]+$/,
                                                    })}
                                                    maxLength={100}
                                                    aria-required="true"
                                                />
                                                {errors.streetName && errors.streetName.type === "pattern" && (
                                                    <span className="form-error">
                                                        Invalid format. Senior's Street Name can only include letters, numbers, spaces and the special characters: -
                                                    </span>
                                                )}
                                                {errors.streetName && errors.streetName.type === "required" && (
                                                    <span className="form-error">Senior's Street Name is required.</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="form-row row">
                                        <div className="form-row inline row">
                                            <div className="form-label col-xs-12 col-sm-5 col-no-gutter"></div>
                                            <div className="form-input col-xs-12 col-sm-7 col-no-gutter">
                                                <small>We do not require city, state or postal code.</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="dialog-footer">
                            <div className="row between-xs">
                                <div className="col-xs col-no-gutter flex-grow-null">
                                    <button type="button" className="btn-default btn-link btn-no-padding btn-no-border" onClick={() => closeDialog()}>
                                        <span className="btn-text">Cancel</span>
                                    </button>
                                </div>
                                <div className="col-xs col-no-gutter flex-grow-null">
                                    <button type="submit" className="btn-default btn-primary">
                                        <span className="btn-text">Save</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </>
            ) : (
                <div className="box loading"></div>
            )}
        </React.Fragment>
    );
};

export default SeniorsFraudResolutionDialog;
